import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, type Invoice, type InvoiceInput, type InvoiceStatus } from '../lib/api';
import {
  addDaysIso,
  formatDate,
  formatMoney,
  INVOICE_STATUS_LABELS,
  todayIso,
} from '../lib/format';
import { Badge, Button, Card, ErrorText, Field, inputClass, Modal } from './ui';

function statusBadge(inv: Invoice) {
  if (inv.overdue) return <Badge color="red">Restantă</Badge>;
  const color = { DRAFT: 'slate', SENT: 'violet', PAID: 'green', CANCELLED: 'slate' } as const;
  return <Badge color={color[inv.status]}>{INVOICE_STATUS_LABELS[inv.status]}</Badge>;
}

function InvoiceForm({
  initial,
  onSubmit,
}: {
  initial?: Invoice;
  onSubmit: (input: InvoiceInput) => Promise<void>;
}) {
  const [number, setNumber] = useState(initial?.number ?? '');
  const [issuedAt, setIssuedAt] = useState(initial?.issuedAt ?? todayIso());
  const [dueAt, setDueAt] = useState(initial?.dueAt ?? addDaysIso(15));
  const [amount, setAmount] = useState(initial ? String(initial.amount) : '');
  const [currency, setCurrency] = useState(initial?.currency ?? 'RON');
  const [status, setStatus] = useState<InvoiceStatus>(initial?.status ?? 'SENT');
  const [externalUrl, setExternalUrl] = useState(initial?.externalUrl ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        number,
        issuedAt,
        dueAt,
        amount: Number(amount.replace(',', '.')),
        currency,
        status,
        externalUrl: externalUrl || null,
        notes: notes || null,
      });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Număr factură">
          <input
            required
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className={inputClass}
            placeholder="CCC 0123"
          />
        </Field>
        <Field label="Status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatus)}
            className={inputClass}
          >
            {(Object.keys(INVOICE_STATUS_LABELS) as InvoiceStatus[]).map((s) => (
              <option key={s} value={s}>
                {INVOICE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Data emiterii">
          <input
            type="date"
            required
            value={issuedAt}
            onChange={(e) => setIssuedAt(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Scadență">
          <input
            type="date"
            required
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Sumă">
          <input
            required
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputClass}
            placeholder="1000"
          />
        </Field>
        <Field label="Monedă">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className={inputClass}
          >
            <option>RON</option>
            <option>EUR</option>
          </select>
        </Field>
      </div>
      <Field label="Link (SmartBill, PDF)">
        <input
          type="url"
          value={externalUrl}
          onChange={(e) => setExternalUrl(e.target.value)}
          className={inputClass}
          placeholder="https://"
        />
      </Field>
      <Field label="Note">
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={inputClass}
        />
      </Field>
      <ErrorText error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={busy}>
          {busy ? 'Se salvează…' : 'Salvează'}
        </Button>
      </div>
    </form>
  );
}

export function InvoicesTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Invoice | 'new' | null>(null);
  const invoices = useQuery({
    queryKey: ['invoices', tenantId],
    queryFn: () => api.invoices(tenantId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['invoices', tenantId] });
    await queryClient.invalidateQueries({ queryKey: ['tenants'] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteInvoice(id),
    onSuccess: refresh,
  });

  const list = invoices.data ?? [];
  const totals = list
    .filter((i) => i.status === 'SENT')
    .reduce<
      Record<string, number>
    >((acc, i) => ({ ...acc, [i.currency]: (acc[i.currency] ?? 0) + i.amount }), {});

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="text-sm text-slate-400">
          De încasat:{' '}
          {Object.keys(totals).length === 0
            ? 'nimic'
            : Object.entries(totals)
                .map(([c, v]) => formatMoney(v, c))
                .join(' + ')}
        </div>
        <Button className="ml-auto" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Factură
        </Button>
      </div>
      <ErrorText error={invoices.error ?? remove.error} />
      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-3">Număr</th>
              <th className="px-3 py-3">Emisă</th>
              <th className="px-3 py-3">Scadență</th>
              <th className="px-3 py-3 text-right">Sumă</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {list.map((inv) => (
              <tr key={inv.id}>
                <td className="px-4 py-3 font-medium text-white">
                  {inv.number}
                  {inv.notes && (
                    <div className="text-xs font-normal text-slate-400">{inv.notes}</div>
                  )}
                </td>
                <td className="px-3 py-3">{formatDate(inv.issuedAt)}</td>
                <td className="px-3 py-3">{formatDate(inv.dueAt)}</td>
                <td className="px-3 py-3 text-right">{formatMoney(inv.amount, inv.currency)}</td>
                <td className="px-3 py-3">{statusBadge(inv)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {inv.externalUrl && (
                      <a
                        href={inv.externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
                        title="Deschide"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditing(inv)}
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
                      title="Modifică"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Ștergi factura ${inv.number}?`)) remove.mutate(inv.id);
                      }}
                      className="rounded p-1.5 text-slate-400 hover:bg-red-500/20 hover:text-red-300"
                      title="Șterge"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!invoices.isLoading && list.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  Nicio factură încă.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      {editing && (
        <Modal
          title={editing === 'new' ? 'Factură nouă' : `Factura ${editing.number}`}
          onClose={() => setEditing(null)}
        >
          <InvoiceForm
            initial={editing === 'new' ? undefined : editing}
            onSubmit={async (input) => {
              if (editing === 'new') await api.createInvoice(tenantId, input);
              else await api.updateInvoice(editing.id, input);
              await refresh();
              setEditing(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
